import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslate } from "react-admin";

import { SalesforceReportAggregate, SalesforceReportChart } from "../generated/graphql";
import { buildDonutData, paletteColor, SALESFORCE_CHART_COLORS } from "../lib/salesforceDashboard";
import {
  buildDonutSlices,
  buildGroupBars,
  buildMeasureBars,
  buildSeriesBars,
  formatMeasureValue,
  groupKeyValue,
  metricValue,
  type ReportChartData,
} from "../lib/salesforceReport";
import { space } from "../theme/designTokens";
import { SalesforceColumnChart, SalesforceDonutChart, SalesforceEmptyState } from "./SalesforceDashboardCharts";
import { type ReportBarMode, SalesforceReportBarChart } from "./SalesforceReportCharts";

const colors = SALESFORCE_CHART_COLORS;
const METRIC_FONT_SIZE_PX = 48;

const barMode = (chart: SalesforceReportChart): ReportBarMode => {
  switch (chart) {
    case SalesforceReportChart.ColumnGrouped:
    case SalesforceReportChart.ColumnMeasures:
    case SalesforceReportChart.Bar:
      return "grouped";
    case SalesforceReportChart.ColumnStackedToHundred:
      return "percent";
    default:
      return "stacked";
  }
};

const MetricValue = ({ value, label }: { value: string; label: string }) => (
  <Box
    sx={{
      display: "flex",
      flex: 1,
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: `${space.sm}px`,
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: `${METRIC_FONT_SIZE_PX}px`, lineHeight: 1.1, color: colors.textStrong }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "12px", color: colors.textWeak }}>{label}</Typography>
  </Box>
);

type SalesforceReportChartBodyProps = {
  component: ReportChartData;
  // A click on a mark gives the clicked groupings' values, outermost first, null for the empty value; none for the
  // small groups a donut combines. A metric is not clickable.
  onSelect?: (values: Array<string | null>) => void;
};

export const SalesforceReportChartBody = ({ component, onSelect }: SalesforceReportChartBodyProps) => {
  const translate = useTranslate();
  const { chart, groupings, measures, source } = component;

  const fieldLabel = (field: string): string =>
    translate(`salesforceReports.fields.${source ?? ""}.${field}`, { _: field });
  const measureLabels = useMemo(
    () =>
      measures.map(measure =>
        translate(`salesforceReports.measures.${measure.aggregate}`, {
          field: measure.field
            ? translate(`salesforceReports.fields.${source ?? ""}.${measure.field}`, { _: measure.field })
            : "",
        })
      ),
    [measures, source, translate]
  );
  const valueTitle = measureLabels[0] ?? "";
  const categoryTitle = groupings.map(grouping => fieldLabel(grouping.field)).join(" > ");
  const firstGroupingTitle = groupings[0] ? fieldLabel(groupings[0].field) : categoryTitle;
  const secondGroupingTitle = groupings[1] ? fieldLabel(groupings[1].field) : undefined;
  const firstAggregate = measures[0]?.aggregate ?? SalesforceReportAggregate.Count;
  const formatFirst = (value: number): string => formatMeasureValue(firstAggregate, value);

  const bars = useMemo(() => {
    switch (chart) {
      case SalesforceReportChart.Column:
      case SalesforceReportChart.Bar:
        return buildGroupBars(component, valueTitle);
      case SalesforceReportChart.BarStacked:
      case SalesforceReportChart.ColumnGrouped:
      case SalesforceReportChart.ColumnStacked:
      case SalesforceReportChart.ColumnStackedToHundred:
      case SalesforceReportChart.ColumnStackedLine:
        return buildSeriesBars(component);
      case SalesforceReportChart.ColumnMeasures:
        return buildMeasureBars(component, measureLabels);
      default:
        return null;
    }
  }, [chart, component, measureLabels, valueTitle]);
  const donut = useMemo(
    () =>
      chart === SalesforceReportChart.Donut
        ? buildDonutData(buildDonutSlices(component), { combineSmallGroups: true })
        : null,
    [chart, component]
  );

  // The raw values behind the slices' formatted labels.
  const donutGroupKeys = useMemo(
    () => new Map(buildGroupBars(component, "").rows.map(row => [row.label, row.key])),
    [component]
  );

  if (component.points.length === 0) return <SalesforceEmptyState />;

  switch (chart) {
    case SalesforceReportChart.Metric:
      return <MetricValue label={valueTitle} value={formatMeasureValue(firstAggregate, metricValue(component))} />;
    case SalesforceReportChart.Donut:
      return donut ? (
        <SalesforceDonutChart
          data={donut}
          formatValue={formatFirst}
          legendTitle={categoryTitle}
          onSliceClick={
            onSelect
              ? slice => {
                  if (slice.isOther) return onSelect([]);
                  if (slice.label === null) return onSelect([null]);
                  const key = donutGroupKeys.get(slice.label);
                  if (key !== undefined) onSelect([groupKeyValue(key)]);
                  return undefined;
                }
              : undefined
          }
          showPercentages={component.showPercentages}
          valueLabel={valueTitle}
        />
      ) : null;
    case SalesforceReportChart.Column:
      return bars ? (
        <SalesforceColumnChart
          showValues={component.showValues ?? undefined}
          data={bars.rows.map(row => ({ label: row.label, value: row.values[0] ?? null, key: row.key }))}
          onBarClick={onSelect ? datum => onSelect([groupKeyValue(datum.key ?? "")]) : undefined}
          xAxisTitle={categoryTitle}
          yAxisTitle={valueTitle}
        />
      ) : null;
    case SalesforceReportChart.BarStacked:
    case SalesforceReportChart.Bar:
      return bars ? (
        <SalesforceReportBarChart
          showValues={component.showValues ?? undefined}
          bars={bars}
          categoryTitle={categoryTitle}
          firstGroupingTitle={firstGroupingTitle}
          formatValue={formatFirst}
          mode={barMode(chart)}
          onSelect={onSelect}
          orientation="bar"
          legendTitle={chart === SalesforceReportChart.BarStacked ? secondGroupingTitle : undefined}
          valueTitle={valueTitle}
        />
      ) : null;
    case SalesforceReportChart.ColumnMeasures:
      return bars ? (
        <SalesforceReportBarChart
          showValues={component.showValues ?? undefined}
          bars={bars}
          categoryTitle={categoryTitle}
          firstGroupingTitle={firstGroupingTitle}
          formatValue={(value, index) =>
            formatMeasureValue(measures[index]?.aggregate ?? SalesforceReportAggregate.Count, value)
          }
          legendTitle={translate("salesforceReports.measureLegend")}
          mode={barMode(chart)}
          onSelect={onSelect}
          orientation="column"
          seriesAreMeasures
          valueTitle={valueTitle}
        />
      ) : null;
    case SalesforceReportChart.ColumnGrouped:
    case SalesforceReportChart.ColumnStacked:
    case SalesforceReportChart.ColumnStackedToHundred:
    case SalesforceReportChart.ColumnStackedLine:
      return bars ? (
        <SalesforceReportBarChart
          showValues={component.showValues ?? undefined}
          bars={bars}
          categoryTitle={categoryTitle}
          firstGroupingTitle={firstGroupingTitle}
          formatValue={formatFirst}
          legendTitle={secondGroupingTitle}
          line={
            chart === SalesforceReportChart.ColumnStackedLine && measures[1]
              ? {
                  label: measureLabels[1],
                  color: paletteColor(bars.series.length),
                  formatValue: value => formatMeasureValue(measures[1].aggregate, value),
                }
              : undefined
          }
          mode={barMode(chart)}
          onSelect={onSelect}
          orientation="column"
          valueTitle={valueTitle}
        />
      ) : null;
    default:
      return <SalesforceEmptyState />;
  }
};
